import React, { useState, useEffect } from 'react';
import { Brain, Zap, RefreshCw, MapPin } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../store/AuthContext';
import { getApiUrl } from '../../services/api';

const AnalysisResults = ({ mapBounds, onViableFound }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // { viable: [], scanned: N }
  const [params, setParams] = useState({
    radius: 1000,
    safeArea: 1000,
    engines: 1,
    competence: 0
  });
  const [maxParams, setMaxParams] = useState({
    radius: 50000,
    safeArea: 50000
  });

  useEffect(() => {
    const fetchScoutDefaults = async () => {
      try {
        const resp = await axios.get(`${getApiUrl()}/setup/scout-params`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = resp.data.data || {};
        setMaxParams({
          radius: data.scout_scan_radius || 50000,
          safeArea: data.scout_safe_area || 50000
        });
      } catch {
        // use defaults
      }
    };
    if (token) fetchScoutDefaults();
  }, [token]);

  const runScan = async () => {
    if (!mapBounds) return;
    setLoading(true);
    try {
      const bounds = {
        north: mapBounds.getNorthEast().lat(),
        east:  mapBounds.getNorthEast().lng(),
        south: mapBounds.getSouthWest().lat(),
        west:  mapBounds.getSouthWest().lng(),
      };
      const resp = await axios.post(`${getApiUrl()}/analysis/scan`, {
        bounds,
        ...params
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = resp.data;
      setResults(data);
      if (onViableFound) onViableFound(data.viable || []);
    } catch (err) {
      console.error(err);
      alert('Scan failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResults(null);
    if (onViableFound) onViableFound([]);
  };

  const hasResults = results !== null;
  const viable = results?.viable || [];
  const isViable = viable.length > 0;

  return (
    <div style={{
      background: 'rgba(5, 7, 10, 0.9)',
      border: '1px solid #0B3D91',
      padding: '15px',
      borderRadius: '4px',
      color: '#F0F0F0',
      width: '280px',
      boxShadow: '0 0 20px rgba(0,0,0,0.5)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
        <Brain size={18} color="#00CEC9" />
        <h3 style={{ color: '#00CEC9', fontSize: '0.85rem', textTransform: 'uppercase', margin: 0, letterSpacing: '1px' }}>
          Find Opportunity
        </h3>
      </div>

      {/* Sliders always visible */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
        <div style={{ fontSize: '0.7rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
            <span style={{ color: '#888', display: 'flex', alignItems: 'center', gap: '4px' }}>
              SCAN RADIUS (Meters)
              <span
                title="Radio del area sin ninguna Base."
                style={{ cursor: 'help', color: '#555', fontSize: '0.65rem', fontWeight: 'bold', border: '1px solid #555', borderRadius: '50%', width: '12px', height: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
              >?</span>
            </span>
            <span style={{ color: '#E27B58' }}>{params.radius}</span>
          </div>
          <input
            type="range" min="50" max={maxParams.radius} step="50"
            value={params.radius}
            onChange={(e) => { setResults(null); setParams({...params, radius: parseInt(e.target.value)}); }}
            style={{ width: '100%', accentColor: '#E27B58' }}
          />
        </div>

        <div style={{ fontSize: '0.7rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
            <span style={{ color: '#888', display: 'flex', alignItems: 'center', gap: '4px' }}>
              SAFE AREA (Meters)
              <span
                title="Distancia minima a cada uno de los engines o competences, de una de nuestras bases."
                style={{ cursor: 'help', color: '#555', fontSize: '0.65rem', fontWeight: 'bold', border: '1px solid #555', borderRadius: '50%', width: '12px', height: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
              >?</span>
            </span>
            <span style={{ color: '#E27B58' }}>{params.safeArea}</span>
          </div>
          <input
            type="range" min="50" max={maxParams.safeArea} step="50"
            value={params.safeArea}
            onChange={(e) => { setResults(null); setParams({...params, safeArea: parseInt(e.target.value)}); }}
            style={{ width: '100%', accentColor: '#E27B58' }}
          />
        </div>

        <div style={{ fontSize: '0.7rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
            <span style={{ color: '#888', display: 'flex', alignItems: 'center', gap: '4px' }}>
              Engines
              <span
                title="Minimo de Engines dentro del area de oportunidad"
                style={{ cursor: 'help', color: '#555', fontSize: '0.65rem', fontWeight: 'bold', border: '1px solid #555', borderRadius: '50%', width: '12px', height: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
              >?</span>
            </span>
            <span style={{ color: '#4CAF50' }}>{params.engines}</span>
          </div>
          <input
            type="range" min="0" max="10" step="1"
            value={params.engines}
            onChange={(e) => { setResults(null); setParams({...params, engines: parseInt(e.target.value)}); }}
            style={{ width: '100%', accentColor: '#4CAF50' }}
          />
        </div>

        <div style={{ fontSize: '0.7rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
            <span style={{ color: '#888', display: 'flex', alignItems: 'center', gap: '4px' }}>
              Competence
              <span
                title="Minimo de Competencia dentro del area de oportunidad"
                style={{ cursor: 'help', color: '#555', fontSize: '0.65rem', fontWeight: 'bold', border: '1px solid #555', borderRadius: '50%', width: '12px', height: '12px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
              >?</span>
            </span>
            <span style={{ color: '#FF5252' }}>{params.competence}</span>
          </div>
          <input
            type="range" min="0" max="10" step="1"
            value={params.competence}
            onChange={(e) => { setResults(null); setParams({...params, competence: parseInt(e.target.value)}); }}
            style={{ width: '100%', accentColor: '#FF5252' }}
          />
        </div>
      </div>

      {!mapBounds && (
        <p style={{ fontSize: '0.7rem', color: '#636e72', margin: '0 0 10px', fontFamily: 'sans-serif', textAlign: 'center' }}>
          Move the map to activate area scan.
        </p>
      )}

      {/* Scan button */}
      <button
        onClick={runScan}
        disabled={loading || !mapBounds || (params.engines === 0 && params.competence === 0)}
        className="nasa-button"
        style={{ width: '100%', fontSize: '0.75rem', display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: hasResults ? '12px' : '0' }}
      >
        {loading ? 'SCANNING AREA...' : (
          <>
            <Zap size={14} /> SCAN VISIBLE AREA
          </>
        )}
      </button>

      {/* Results */}
      {hasResults && (
        <div>
          {/* Summary badge */}
          <div style={{
            textAlign: 'center',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '10px',
            background: isViable ? 'rgba(0,200,100,0.08)' : 'rgba(255,100,100,0.08)',
            border: `1px solid ${isViable ? '#00b894' : '#d63031'}`
          }}>
            <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: isViable ? '#00b894' : '#d63031' }}>
              {isViable ? `${viable.length} VIABLE` : 'NONE FOUND'}
            </div>
            <div style={{ fontSize: '0.65rem', color: '#888', textTransform: 'uppercase', marginTop: '4px' }}>
              {results.scanned} engines scanned · {params.radius}m radius
            </div>
          </div>

          {/* Viable zones list */}
          {viable.length > 0 && (
            <div style={{ maxHeight: '160px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '10px' }}>
              {viable.map((v, i) => (
                <div key={i} style={{
                  background: 'rgba(0,184,148,0.07)',
                  border: '1px solid rgba(0,184,148,0.25)',
                  borderRadius: '3px',
                  padding: '6px 8px',
                  fontSize: '0.68rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                    <MapPin size={10} color="#00b894" style={{ flexShrink: 0 }} />
                    <span style={{ color: '#ddd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {v.brand || v.name || `Zone ${i + 1}`}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '6px' }}>
                    <span style={{ color: '#4CAF50', fontSize: '0.65rem' }}>E:{v.engineCount}</span>
                    <span style={{ color: '#FF5252', fontSize: '0.65rem' }}>C:{v.competenceCount}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={reset}
            style={{
              width: '100%', background: 'none', border: 'none',
              color: '#555', fontSize: '0.7rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'
            }}
          >
            <RefreshCw size={11} /> RESET
          </button>
        </div>
      )}
    </div>
  );
};

export default AnalysisResults;

